import React, { useState, useCallback, useMemo, useContext } from "react";
import { Col, Container, Row, Card, CardBody, CardHeader } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb/BreadCrumb";
import TableContainerMin from "../../../Components/Common/TableContainerMin";
import { useQuery, useQueryClient } from "react-query";
import {
  CarOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  EditOutlined,
  HistoryOutlined,
  LinkOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { Button, Modal, Spin, Tag } from "antd";
import HistoryContext from "../../../Components/Hooks/HistoryContext";
import toast from "react-hot-toast";
import {
  deleteVehicule,
  getVehicleHistory,
  getVehicules,
} from "../services/vehiculeApi";
import AddEditVehicleForm from "../component/AddEditVehiclesForm";
import "../../Styles/style.css";
import AffectVehicleForm from "../component/AffectVehicleToUser";
import { getUserById } from "../../Users/services/userApi";
import HistoryVehicle from "../component/HistoryVehicle";
import AddVehMass from "../component/file/AddMassVehicles";
import VehicleSummaryCards from "../component/VehicleSummaryCards";

const Vehicules = () => {
  const history = useContext(HistoryContext);
  const queryClient = useQueryClient();

  document.title = `Véhicules | ${process.env.REACT_APP_NAME} - ${process.env.REACT_APP_DESCRIPTION}`;

  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [isAffectModalOpen, setIsAffectModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [driverName, setDriverName] = useState(null);
  const [selectedVehicule, setSelectedVehicule] = useState(null);
  const [selectedVehHistory, setSelectedVehHistory] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteVehicleName, setDeleteVehicleName] = useState("");
  const [bulkAddModalOpen, setBulkAddModalOpen] = useState(false);

  const { isLoading, isError, error, data: vehicules } = useQuery("vehicules", getVehicules);

  const toggleAddEditModal = useCallback(
    (vehicule = null) => {
      setSelectedVehicule(vehicule);
      setIsAddEditModalOpen((prev) => !prev);
      if (isAffectModalOpen) setIsAffectModalOpen(false);
    },
    [isAffectModalOpen]
  );

  const handleEdit = (vehicule) => {
    toggleAddEditModal(vehicule);
  };

  const toggleBulkAddModal = () => setBulkAddModalOpen(!bulkAddModalOpen);

  const toggleHistoryModal = useCallback(
    async (vehicule = null) => {
      setSelectedVehicule(vehicule);
      try {
        const oldDriver = await getVehicleHistory(vehicule?.id);
        setSelectedVehHistory(oldDriver);
      } catch (error) {
        console.error("Error fetching vehicle history:", error);
      }
      setIsHistoryModalOpen((prev) => !prev);
      if (isAddEditModalOpen) setIsAddEditModalOpen(false);
      if (isAffectModalOpen) setIsAffectModalOpen(false);
    },
    [isAddEditModalOpen, isAffectModalOpen]
  );

  const handleHistory = (vehicule) => {
    toggleHistoryModal(vehicule);
  };

  const toggleAffectModal = useCallback(
    (vehicule = null) => {
      setSelectedVehicule(vehicule);
      setIsAffectModalOpen((prev) => !prev);
      if (isAddEditModalOpen) setIsAddEditModalOpen(false);
    },
    [isAddEditModalOpen]
  );

  const handleAffect = async (vehicule) => {
    try {
      const selectedDriverVeh = await getUserById(vehicule?.actualUserId);
      setDriverName(selectedDriverVeh?.name || 'Pas de chauffeur');
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
    toggleAffectModal(vehicule);
  };

  const handleDelete = async (vehiculeId) => {
    try {
      await deleteVehicule(vehiculeId);
      queryClient.invalidateQueries("vehicules");
      toast.success("Le véhicule a été supprimé avec succès 🎉");
    } catch (error) {
      console.error("Error deleting vehicule:", error);
      toast.error(
        `😔 Erreur lors de la suppression du véhicule : ${
          error?.message || error
        }`
      );
    } finally {
      setDeleteConfirmModal(false);
    }
  };

  const openDeleteConfirmModal = (vehiculeId) => {
    const vehiculeToDelete = vehicules.find(
      (vehicule) => vehicule.id === vehiculeId
    );
    if (vehiculeToDelete) {
      setDeleteTargetId(vehiculeId);
      setDeleteVehicleName(vehiculeToDelete.brand);
      setDeleteConfirmModal(true);
    }
  };

  const closeDeleteConfirmModal = () => {
    setDeleteConfirmModal(false);
  };

  const columns = useMemo(() => [
      { Header: "Marque", accessor: "brand", filterable: false },
      { Header: "Modele", accessor: "model", filterable: false },
      { Header: "Plaque", accessor: "registrationNumber", filterable: false },
      {
        Header: "Mise en service",
        accessor: "commissioningDate",
        Cell: ({ value }) =>
          value ? new Date(value).toLocaleDateString("fr-FR") : "-",
        filterable: false,
      },
      // {
      //   Header: "Chauffeur",
      //   accessor: "actualUserId",
      //   Cell: ({ row }) => {
      //     const { actualUserId } = row.original;
      //     const { data: driver, isLoading: driverLoading } = useQuery(
      //       ["driver", actualUserId],
      //       () => getUserById(actualUserId),
      //       {
      //         enabled: !!actualUserId,
      //       }
      //     );

      //     if (driverLoading) {
      //       return <Spin tip="Chargement..." size="small" />;
      //     }

      //     if (driver) {
      //       return <div>{driver.name}</div>;
      //     }
      //     return <div>-</div>;
      //   },
      //   filterable: false,
      // },
      {
        Header: "Chauffeur",
        accessor: "actualUserName",
        Cell: ({ row }) => (
          <div>{row.original.actualUserName ? `${row.original.actualUserName}` : "-"}</div>
        ),
        filterable: false,
      },
      {
        Header: "Statut",
        accessor: "statut",
        Cell: ({ value }) => {
          const statusColors = {
            "En service": "success",
            "Libre": "info",
            "Hors service": "danger",
          };

          const color = statusColors[value] || "secondary";

          return (
            <span className={`badge bg-${color}-subtle text-${color}`}>
              {value}
            </span>
          );
        },
        filterable: false,
      },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <Button
              title="Affecter à un chauffeur"
              icon={<LinkOutlined />}
              size="sm"
              onClick={() => handleAffect(row.original)}
              className="btn btn-soft-dark"
            />
            <EditOutlined title="Modifier" size="sm" onClick={() => handleEdit(row.original)} className="btn btn-soft-warning" />
            <DeleteOutlined title="Supprimer" size="sm" onClick={() => openDeleteConfirmModal(row.original.id)} className="btn btn-soft-danger" />
            <HistoryOutlined title="Détail du véhicule" size="sm" onClick={() => handleHistory(row.original)} className="btn btn-soft-primary" />
          </div>
        ),
        filterable: false,
      },
    ],
    [ handleEdit, openDeleteConfirmModal, handleAffect, handleHistory]
  );

  const renderTabs = () => (
    <div className="d-flex gap-3 mb-3">
      {tabs.map((tab) => (
        <Button key={tab} onClick={() => handleTabChange(tab)} className={`btn ${activeTab == tab ? "active-tab" : "btn-light"}`}>
          {tab}
        </Button>
      ))}
    </div>
  );

  const HeaderTable = () => (
    <div className="d-flex justify-content-between align-items-center w-100">
      <div className="d-flex gap-3">{renderTabs()}</div>
      <div className="d-flex gap-3">
        <Button type="button" className="active-tab" title="Ajouter plusieurs véhicules" onClick={toggleBulkAddModal}>
          Ajout en masse <CloudUploadOutlined />
        </Button>
        <Button type="button" className="active-tab" title="Ajout d'un seul Véhicule" onClick={() => toggleAddEditModal(null)}>
          Ajouter un <CarOutlined />
        </Button>
      </div>
    </div>
  );

  const tabs = ["Tous", "En service", "Hors service", "Libre"];
  const [activeTab, setActiveTab] = useState("Tous");

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const filteredVehicules = useMemo(() => {
    if (!vehicules) return error?.message || [];
    switch (activeTab) {
      case "En service":
        return vehicules.filter((v) => v.statut === "En service");
      case "Hors service":
        return vehicules.filter((v) => v.statut === "Hors service");
      case "Libre":
        return vehicules.filter((v) => v.statut === "Libre");
      case "Autre":
        return vehicules.filter((v) => v.actualUserId === null);
      default:
        return vehicules;
    }
  }, [vehicules, activeTab]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb page="Véhicules" history={history} />
          <Row>
            <Col>
              <Card id="contactList">
                <CardHeader>
                  <div className="text-title mb-4">
                    Mes vehicules {" "}
                    <Tag color="#5ea8adff" style={{ fontSize: "12px" }}>
                      {filteredVehicules?.length || 0}
                    </Tag>
                  </div>
                  <VehicleSummaryCards vehicles={filteredVehicules} />
                  <HeaderTable />
                </CardHeader>
                <CardBody className="pt-0">
                  <TableContainerMin
                    columns={columns}
                    data={filteredVehicules}
                    isGlobalSearch={true}
                    isGlobalFilter={true}
                    customPageSize={10}
                    className="custom-header-css"
                    divClass="table-responsive table-card mb-3"
                    tableClass="align-middle table-nowrap"
                    theadClass="table-light"
                    HeaderTable={HeaderTable}
                  />
                  {isLoading && !isError && (
                    <div className="text-center">
                      Chargement, Merci de patienter ... <Spin />
                    </div>
                  )}
                  {filteredVehicules &&
                    filteredVehicules?.length === 0 &&
                    !isLoading && (
                      <div className="text-center">
                        Aucun véhicule {activeTab} à afficher pour le moment.
                      </div>
                    )}
                </CardBody>
              </Card>
            </Col>
          </Row>
          <AddEditVehicleForm
            modal={isAddEditModalOpen}
            setModal={setIsAddEditModalOpen}
            toggle={toggleAddEditModal}
            selectedVehicule={selectedVehicule}
          />
          <AffectVehicleForm
            modal={isAffectModalOpen}
            setModal={setIsAffectModalOpen}
            toggle={toggleAffectModal}
            selectedVehicule={selectedVehicule}
            actualDriver={driverName}
          />
          <HistoryVehicle
            modal={isHistoryModalOpen}
            setModal={setIsHistoryModalOpen}
            toggle={toggleHistoryModal}
            selectedVehicule={selectedVehicule}
            selectedVehHistory={selectedVehHistory}
          />
          <AddVehMass
            modal={bulkAddModalOpen}
            setModal={setBulkAddModalOpen}
            toggle={toggleBulkAddModal}
          />
          <Modal
            title="Confirmer la suppression"
            visible={deleteConfirmModal}
            onCancel={closeDeleteConfirmModal}
            footer={[
              <Button key="cancel" onClick={closeDeleteConfirmModal}>
                Annuler
              </Button>,
              <Button
                key="delete"
                type="danger"
                onClick={() => handleDelete(deleteTargetId)}
              >
                Supprimer
              </Button>,
            ]}
          >
            <p>
              Êtes-vous sûr de vouloir supprimer le véhicule {deleteVehicleName}{" "} ?
            </p>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Vehicules;
